/**
 * DaocHud.ts - DAoC 스타일 월드 HUD 배치 시스템
 *
 * - HudLayout.wrap(): 기존 HUD 요소를 "창"으로 감싸 좌상단 모서리 드래그 이동 / 우하단 모서리 드래그 크기조절 /
 *   위치·크기 localStorage 저장 / 화면 크기 변경 시 보정을 제공한다.
 * - MinimapHUD: 우상단 원형 미니맵
 * - GroupHUD: 그룹 창 (솔로 시작 안내)
 */

import { EntityState } from '../../core/Types';
import { ZoneData } from '../world/ZoneMapData';
import { getTileTypeAt, TILE_PROPERTY_SPECS } from '../world/TilePropertySystem';

type Anchor = 'tl' | 'tr' | 'bl' | 'br' | 'tc' | 'bc';

export interface HudWinOptions {
  id: string;
  /** scale: 내용 전체를 비율 확대/축소, free: 창 크기를 직접 변경 */
  mode: 'scale' | 'free';
  anchor: Anchor;
  dx: number;
  dy: number;
  /** 기본 너비(px). scale 모드에서는 생략하면 내용 크기 */
  w?: number;
  /** free 모드 기본 높이(px) */
  h?: number;
  /** scale 모드 기본 배율 */
  scale?: number;
  minW?: number;
  minH?: number;
  /** 가로세로 1:1 유지 (미니맵) */
  square?: boolean;
  /** 원형 프레임 (미니맵) */
  round?: boolean;
}

interface SavedLayout {
  l: number;
  t: number;
  w?: number;
  h?: number;
  s?: number;
}

interface HudWin {
  opts: HudWinOptions;
  wrapper: HTMLElement;
  inner: HTMLElement;
  placed: boolean;
  busy: boolean;
}

const STORAGE_KEY = 'terra_anima_hud_layout_v1';
const Z_BASE = 50;
const Z_MAX = 85; // 캐릭터창(90) 등 모달보다 아래

function injectStyles(): void {
  if (document.getElementById('daoc-hud-style')) return;
  const st = document.createElement('style');
  st.id = 'daoc-hud-style';
  st.textContent = `
    .hud-win{position:absolute;box-sizing:border-box;background:linear-gradient(#2b2218,#1b150e);
      border:2px solid #7a5c2e;border-radius:4px;box-shadow:0 0 0 1px #0008,inset 0 0 0 1px #c9a45a33,0 6px 18px rgba(0,0,0,.45);
      color:#e8dcc0;touch-action:none}
    .hud-win.round{border-radius:50%}
    .hud-win > .hud-inner{position:relative!important;left:auto!important;top:auto!important;right:auto!important;bottom:auto!important;
      transform:none!important;margin:0!important;width:100%!important;background:transparent!important;border:none!important;
      box-shadow:none!important;backdrop-filter:none!important;border-radius:inherit}
    .hud-win.fill > .hud-inner{height:100%!important}
    .hud-grab,.hud-rs{position:absolute;width:18px;height:18px;z-index:5;opacity:.8;touch-action:none}
    .hud-grab:hover,.hud-rs:hover{opacity:1}
    .hud-grab{left:-2px;top:-2px;cursor:move;background:linear-gradient(135deg,#f3d98b 0 45%,transparent 45%);border-top-left-radius:3px}
    .hud-rs{right:-2px;bottom:-2px;cursor:nwse-resize;background:linear-gradient(315deg,#f3d98b 0 45%,transparent 45%);border-bottom-right-radius:3px}
    .hud-win.round .hud-grab{left:14px;top:14px}
    .hud-win.round .hud-rs{right:14px;bottom:14px}
    .daoc-menu-grid{display:flex;flex-direction:row;align-items:center;gap:4px;padding:4px 6px;width:100%;height:100%;box-sizing:border-box;overflow:hidden;white-space:nowrap}
    .daoc-menu-grid::-webkit-scrollbar{display:none!important;width:0!important;height:0!important}
    #top-nav-bar{overflow:hidden!important}
    #top-nav-bar::-webkit-scrollbar{display:none!important;width:0!important;height:0!important}
    .daoc-mbtn{background:#0c0905;border:1px solid #5a431f;border-radius:3px;color:#cdb98a;cursor:pointer;padding:4px 8px;
      font-size:10.5px;line-height:1.2;text-align:center;font-family:inherit;flex-shrink:0;min-width:44px;}
    .daoc-mbtn:hover{border-color:#f3d98b;color:#fff}
    .daoc-mbtn b{display:block;font-size:16px;line-height:18px}
    .daoc-mbtn i{display:block;min-height:11px;font-style:normal;color:#f3d98b;font-size:9px}
    .daoc-gm{background:linear-gradient(#7f1d1d,#450a0a);border-color:#f87171;color:#fff;padding:4px 8px}
    .daoc-title{font-size:11px;font-weight:bold;color:#f3d98b;padding:3px 8px 3px 24px;background:linear-gradient(#5a431f,#3a2a12);
      border-bottom:1px solid #000;letter-spacing:.5px;display:flex;justify-content:space-between}
  `;
  document.head.appendChild(st);
}

function loadAll(): Record<string, SavedLayout> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

const wins: HudWin[] = [];
let zCounter = Z_BASE;
let resizeBound = false;

function bringToFront(w: HudWin): void {
  zCounter++;
  if (zCounter > Z_MAX) {
    // 순서를 유지한 채 z-index 재배열
    const order = [...wins].sort((a, b) => Number(a.wrapper.style.zIndex) - Number(b.wrapper.style.zIndex));
    order.forEach((o, i) => (o.wrapper.style.zIndex = String(Z_BASE + i)));
    zCounter = Z_BASE + order.length;
  }
  w.wrapper.style.zIndex = String(zCounter);
}

function vw(): number {
  return window.innerWidth;
}
function vh(): number {
  return window.innerHeight;
}

function visSize(w: HudWin): { w: number; h: number; s: number } {
  const s = w.opts.mode === 'scale' ? parseFloat(w.wrapper.dataset.s || '1') : 1;
  return { w: w.wrapper.offsetWidth * s, h: w.wrapper.offsetHeight * s, s };
}

function clampPos(w: HudWin, l: number, t: number): { l: number; t: number } {
  const v = visSize(w);
  return {
    l: Math.max(0, Math.min(Math.max(0, vw() - v.w), l)),
    t: Math.max(0, Math.min(Math.max(0, vh() - v.h), t)),
  };
}

function setScale(w: HudWin, s: number): void {
  w.wrapper.dataset.s = String(s);
  w.wrapper.style.transformOrigin = '0 0';
  w.wrapper.style.transform = `scale(${s})`;
}

function applyDefaults(w: HudWin): void {
  const o = w.opts;
  const el = w.wrapper;
  if (o.mode === 'scale') {
    el.style.width = o.w ? `${o.w}px` : 'max-content';
    el.style.height = 'auto';
    setScale(w, o.scale ?? 1);
  } else {
    el.style.width = `${Math.min(o.w ?? 240, vw() - 20)}px`;
    el.style.height = o.h ? `${Math.min(o.h, vh() - 20)}px` : 'auto';
  }
  const v = visSize(w);
  let l = o.dx;
  let t = o.dy;
  switch (o.anchor) {
    case 'tr':
      l = vw() - o.dx - v.w;
      break;
    case 'bl':
      t = vh() - o.dy - v.h;
      break;
    case 'br':
      l = vw() - o.dx - v.w;
      t = vh() - o.dy - v.h;
      break;
    case 'tc':
      l = (vw() - v.w) / 2 + o.dx;
      break;
    case 'bc':
      l = (vw() - v.w) / 2 + o.dx;
      t = vh() - o.dy - v.h;
      break;
  }
  const c = clampPos(w, l, t);
  el.style.left = `${c.l}px`;
  el.style.top = `${c.t}px`;
}

function applySaved(w: HudWin, s: SavedLayout): void {
  const o = w.opts;
  if (o.mode === 'scale') {
    w.wrapper.style.width = o.w ? `${o.w}px` : 'max-content';
    w.wrapper.style.height = 'auto';
    setScale(w, s.s ?? o.scale ?? 1);
  } else {
    w.wrapper.style.width = `${Math.min(s.w ?? o.w ?? 240, vw() - 4)}px`;
    w.wrapper.style.height = s.h ? `${s.h}px` : o.h ? `${o.h}px` : 'auto';
  }
  const c = clampPos(w, s.l, s.t);
  w.wrapper.style.left = `${c.l}px`;
  w.wrapper.style.top = `${c.t}px`;
}

function place(w: HudWin): void {
  if (w.wrapper.style.display === 'none') return;
  const saved = loadAll()[w.opts.id];
  if (saved) applySaved(w, saved);
  else applyDefaults(w);
  w.placed = true;
}

function save(w: HudWin): void {
  const all = loadAll();
  const v = visSize(w);
  const rec: SavedLayout = { l: w.wrapper.offsetLeft, t: w.wrapper.offsetTop };
  if (w.opts.mode === 'scale') rec.s = v.s;
  else {
    rec.w = w.wrapper.offsetWidth;
    rec.h = w.wrapper.offsetHeight;
  }
  all[w.opts.id] = rec;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

function startDrag(w: HudWin, e: PointerEvent, mode: 'move' | 'size'): void {
  e.preventDefault();
  e.stopPropagation();
  bringToFront(w);
  w.busy = true;
  const target = e.currentTarget as HTMLElement;
  target.setPointerCapture(e.pointerId);
  const sx = e.clientX;
  const sy = e.clientY;
  const L = w.wrapper.offsetLeft;
  const T = w.wrapper.offsetTop;
  const W = w.wrapper.offsetWidth;
  const H = w.wrapper.offsetHeight;
  const v0 = visSize(w);
  const o = w.opts;

  const move = (m: PointerEvent): void => {
    const dx = m.clientX - sx;
    const dy = m.clientY - sy;
    if (mode === 'move') {
      const c = clampPos(w, L + dx, T + dy);
      w.wrapper.style.left = `${c.l}px`;
      w.wrapper.style.top = `${c.t}px`;
      return;
    }
    if (o.mode === 'scale') {
      const raw = (v0.w + dx) / Math.max(1, W);
      const maxByScreen = (vw() - L) / Math.max(1, W);
      setScale(w, Math.max(0.5, Math.min(2.2, maxByScreen, raw)));
    } else {
      const minW = o.minW ?? 140;
      const minH = o.minH ?? 40;
      let nw = Math.max(minW, Math.min(vw() - L, W + dx));
      let nh = Math.max(minH, Math.min(vh() - T, H + dy));
      if (o.square) {
        nw = nh = Math.max(minW, Math.min(nw, nh));
      }
      w.wrapper.style.width = `${nw}px`;
      w.wrapper.style.height = `${nh}px`;
    }
  };
  const up = (): void => {
    target.removeEventListener('pointermove', move);
    target.removeEventListener('pointerup', up);
    target.removeEventListener('pointercancel', up);
    w.busy = false;
    save(w);
  };
  target.addEventListener('pointermove', move);
  target.addEventListener('pointerup', up);
  target.addEventListener('pointercancel', up);
}

export const HudLayout = {
  /** 기존 HUD 요소를 이동/크기조절 가능한 창으로 감싼다. */
  wrap(inner: HTMLElement, opts: HudWinOptions): HTMLElement {
    injectStyles();
    const wrapper = document.createElement('div');
    wrapper.className = 'hud-win' + (opts.round ? ' round' : '') + (opts.mode === 'free' ? ' fill' : '');
    wrapper.id = `hudwin-${opts.id}`;
    wrapper.style.zIndex = String(Z_BASE);
    inner.classList.add('hud-inner');

    const parent = inner.parentElement || document.body;
    parent.insertBefore(wrapper, inner);
    wrapper.appendChild(inner);

    const grab = document.createElement('div');
    grab.className = 'hud-grab';
    grab.title = '드래그해서 이동';
    const rs = document.createElement('div');
    rs.className = 'hud-rs';
    rs.title = '드래그해서 크기 조절';
    wrapper.append(grab, rs);

    const win: HudWin = { opts, wrapper, inner, placed: false, busy: false };
    wins.push(win);

    grab.addEventListener('pointerdown', (e) => startDrag(win, e, 'move'));
    rs.addEventListener('pointerdown', (e) => startDrag(win, e, 'size'));
    wrapper.addEventListener('pointerdown', () => bringToFront(win), true);

    // 내부 요소의 표시/숨김(display)을 창에 반영
    const sync = (): void => {
      const hidden = inner.style.display === 'none';
      wrapper.style.display = hidden ? 'none' : '';
      if (!hidden && !win.placed) place(win);
    };
    new MutationObserver(sync).observe(inner, { attributes: true, attributeFilter: ['style'] });
    sync();

    // 내용 크기가 바뀌면(카드 추가 등) 기본 앵커 위치를 다시 계산하고, 저장된 위치는 화면 안으로 보정
    new ResizeObserver(() => {
      if (!win.placed || win.busy || wrapper.style.display === 'none') return;
      if (loadAll()[opts.id]) {
        const c = clampPos(win, wrapper.offsetLeft, wrapper.offsetTop);
        wrapper.style.left = `${c.l}px`;
        wrapper.style.top = `${c.t}px`;
      } else {
        applyDefaults(win);
      }
    }).observe(wrapper);

    if (!resizeBound) {
      resizeBound = true;
      window.addEventListener('resize', () => {
        for (const w of wins) {
          w.placed = false;
          place(w);
        }
      });
    }
    return wrapper;
  },

  /** 지정한 창을 맨 앞으로 */
  focus(id: string): void {
    const w = wins.find((x) => x.opts.id === id);
    if (w) bringToFront(w);
  },

  /** 저장된 HUD 배치를 지우고 기본 배치로 되돌린다. */
  resetAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    for (const w of wins) {
      w.placed = false;
      place(w);
    }
  },
};

/* ------------------------------------------------------------------ 미니맵 */

export function tileColor(type: string): string {
  if (/water|river|lake|sea/.test(type)) return '#1d4f8f';
  if (/lava|fire/.test(type)) return '#b8321a';
  if (/snow|ice|glacier/.test(type)) return '#cfe3f5';
  if (/sand|desert/.test(type)) return '#c9a96a';
  if (/swamp|marsh|poison/.test(type)) return '#4b5a2c';
  if (/road|dirt|cobble|pavement|bridge|gate/.test(type)) return '#8a6a3c';
  if (/wall|roof|megalith|cliff|mountain/.test(type)) return '#5d6672';
  if (/farm/.test(type)) return '#b59a2f';
  const spec = (TILE_PROPERTY_SPECS as Record<string, { walkable: boolean }>)[type];
  if (spec && !spec.walkable) return '#4a5058';
  return '#3b6a2c';
}

export class MinimapHUD {
  private el: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private acc = 0;
  private readonly radiusTiles = 36;

  public mount(parent: HTMLElement): void {
    const el = document.createElement('div');
    el.id = 'minimap-hud';
    el.style.display = 'none';
    el.style.overflow = 'hidden';
    const c = document.createElement('canvas');
    c.width = 180;
    c.height = 180;
    c.style.cssText = 'width:100%;height:100%;display:block;border-radius:50%;';
    el.appendChild(c);
    parent.appendChild(el);
    this.el = el;
    this.canvas = c;
    HudLayout.wrap(el, { id: 'minimap', mode: 'free', anchor: 'tr', dx: 10, dy: 10, w: 150, h: 150, minW: 90, minH: 90, square: true, round: true });
  }

  public update(player: EntityState, zone: ZoneData, dt: number): void {
    if (!this.canvas || !this.el || this.el.style.display === 'none') return;
    this.acc += dt;
    if (this.acc < 0.25) return;
    this.acc = 0;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const W = this.canvas.width;
    const R = this.radiusTiles;
    const px = Math.round(player.x);
    const py = Math.round(player.y);
    const ts = W / (R * 1.5); // 타일 한 칸 픽셀

    ctx.clearRect(0, 0, W, W);
    ctx.fillStyle = '#10180d';
    ctx.fillRect(0, 0, W, W);
    ctx.save();
    ctx.translate(W / 2, W / 2);
    ctx.rotate(Math.PI / 4); // 아이소 화면 방향과 일치 (+x: 우하단)
    for (let dy = -R; dy <= R; dy++) {
      for (let dx = -R; dx <= R; dx++) {
        const tx = px + dx;
        const ty = py + dy;
        if (tx < 0 || ty < 0 || tx >= zone.width || ty >= zone.height) continue;
        ctx.fillStyle = tileColor(getTileTypeAt(zone, tx, ty));
        ctx.fillRect(dx * ts, dy * ts, ts + 0.6, ts + 0.6);
      }
    }
    ctx.restore();
    ctx.beginPath();
    ctx.arc(W / 2, W / 2, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 6;
    ctx.fill();
  }
}

/* ------------------------------------------------------------------ 그룹 창 */

export class GroupHUD {
  private countEl: HTMLElement | null = null;
  private bodyEl: HTMLElement | null = null;
  private lastKey = '';

  public mount(parent: HTMLElement): void {
    const el = document.createElement('div');
    el.id = 'group-hud';
    el.style.display = 'none';
    el.style.flexDirection = 'column';
    el.innerHTML = `
      <div class="daoc-title" style="flex-shrink:0;"><span>그룹</span><span id="group-count">1 / 1 (솔로)</span></div>
      <div id="group-body" style="padding:6px 8px;font-size:10.5px;color:#8a7a55;line-height:1.4;flex:1;min-height:0;overflow-y:auto;">
        그룹원이 없습니다 — 파티를 결성하면 이 창에 표시됩니다.
      </div>`;
    parent.appendChild(el);
    this.countEl = el.querySelector('#group-count');
    this.bodyEl = el.querySelector('#group-body');
    HudLayout.wrap(el, { id: 'group', mode: 'free', anchor: 'tl', dx: 10, dy: 122, w: 230, h: 92, minW: 140, minH: 44 });
  }

  /** 파티가 아니면 솔로 안내, 파티면 파티원 목록(이름/HP/거리)을 표시 */
  public update(members: Array<{ name: string; hp: number; leader: boolean; distText?: string }>, inParty: boolean): void {
    if (!this.bodyEl || !this.countEl) return;
    const key = inParty ? members.map((m) => `${m.name}:${m.hp}:${m.leader}:${m.distText || ''}`).join('|') : 'solo';
    if (key === this.lastKey) return;
    this.lastKey = key;
    if (!inParty) {
      this.countEl.textContent = '1 / 1 (솔로)';
      this.bodyEl.style.color = '#8a7a55';
      this.bodyEl.textContent = '그룹원이 없습니다 — 파티를 결성하면 이 창에 표시됩니다.';
      return;
    }
    this.countEl.textContent = `${members.length} / 8`;
    this.bodyEl.style.color = '#e8dcc0';
    this.bodyEl.innerHTML = members
      .map(
        (m) => `<div style="margin-bottom:5px;">
          <div style="display:flex;justify-content:space-between;align-items:center;font-size:11px;">
            <span>${m.leader ? '👑 ' : ''}<b>${m.name}</b></span>
            <div style="font-size:10px;">
              ${m.distText ? `<span style="color:#38bdf8;margin-right:6px;">${m.distText}</span>` : ''}
              <span style="color:#fcd34d;">${m.hp}%</span>
            </div>
          </div>
          <div style="height:5px;background:#000;border-radius:2px;overflow:hidden;margin-top:2px;">
            <div style="width:${Math.max(0, Math.min(100, m.hp))}%;height:100%;background:linear-gradient(90deg,#ef4444,#10b981);transition:width 0.2s;"></div>
          </div>
        </div>`
      )
      .join('');
  }
}
